import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QrCode, Copy, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatBRL } from "@/lib/format";

/**
 * Dialog for paying one or many plans (main + subaccounts) in a single PIX charge.
 * Props:
 *  - open: bool
 *  - onOpenChange: fn(bool)
 *  - userIds: string[] (the accounts being charged)
 *  - breakdown: [{id, name, beneficiary, username, effective_amount}] (for display)
 *  - onPaid: fn() — called when the PIX status becomes "approved"
 */
export default function BatchPayDialog({ open, onOpenChange, userIds, breakdown, onPaid }) {
  const [loading, setLoading] = useState(false);
  const [pix, setPix] = useState(null);
  const [polling, setPolling] = useState(false);
  const total = breakdown.reduce((acc, b) => acc + Number(b.effective_amount || 0), 0);

  // Reset state when dialog opens, then generate PIX
  useEffect(() => {
    if (!open) { setPix(null); return; }
    if (userIds.length === 0) return;
    setLoading(true); setPix(null);
    api.post("/client/payments/create-pix-batch", { user_ids: userIds })
      .then(({ data }) => setPix(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Erro ao gerar PIX"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll status every 6s
  useEffect(() => {
    if (!pix?.payment_id || !open) return;
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/client/payments/pix/${pix.payment_id}`);
        if (data.status && data.status !== pix.status) {
          setPix((p) => ({ ...p, status: data.status }));
          if (data.status === "approved") {
            toast.success("Pagamento aprovado! 🎉");
            clearInterval(t); setPolling(false);
            setTimeout(() => { onOpenChange(false); onPaid?.(); }, 1400);
          } else if (["cancelled", "rejected"].includes(data.status)) {
            toast.error("Pagamento não aprovado.");
            clearInterval(t); setPolling(false);
          }
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => { clearInterval(t); setPolling(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pix?.payment_id, open]);

  const copyCode = async () => {
    if (!pix?.qr_code) return;
    try {
      await navigator.clipboard.writeText(pix.qr_code);
      toast.success("Código PIX copiado!");
    } catch { toast.error("Não foi possível copiar."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={20} weight="duotone" className="text-blue-600"/>
            Pagar {userIds.length} plano{userIds.length !== 1 && "s"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Itemized */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 divide-y divide-zinc-200">
            {breakdown.map((b) => (
              <div key={b.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`batch-item-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{b.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {b.beneficiary && <>{b.beneficiary} · </>}@{b.username}
                  </div>
                </div>
                <div className="font-display text-sm font-semibold ml-3 tab-nums">{formatBRL(b.effective_amount)}</div>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 bg-zinc-100">
              <div className="text-[11px] tracking-widest uppercase text-zinc-600 font-semibold">Total</div>
              <div className="font-display text-lg font-bold tab-nums">{formatBRL(total)}</div>
            </div>
          </div>

          {/* PIX content */}
          {loading ? (
            <div className="text-center py-8 text-zinc-500 text-sm">Gerando código PIX...</div>
          ) : pix?.qr_code_base64 ? (
            <>
              <div className="rounded-xl border border-zinc-200 bg-white p-4 grid place-items-center">
                <img
                  src={`data:image/png;base64,${pix.qr_code_base64}`}
                  alt="QR Code PIX"
                  data-testid="batch-pix-qr"
                  className="w-52 h-52 object-contain"
                />
              </div>
              <button
                onClick={copyCode}
                data-testid="batch-pix-copy"
                className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2"
              >
                <Copy size={16}/> Copiar código PIX
              </button>
              <div className="text-xs text-zinc-500 text-center">
                Abra o app do seu banco → PIX → Pagar com QR Code ou cole o código.
              </div>
              {polling && pix.status === "pending" && (
                <div className="flex items-center justify-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  Aguardando confirmação...
                </div>
              )}
              {pix.status === "approved" && (
                <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-3 font-medium">
                  <CheckCircle weight="fill" size={18}/> Pagamento confirmado!
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-rose-600 text-sm">
              Não foi possível gerar o PIX. Tente novamente.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="batch-pay-close">
            <X size={14} className="mr-1.5"/> Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
